import { createPortal } from "react-dom";
import { useLayoutEffect, useMemo, useState } from "react";
import styles from "./GuideTourOverlay.module.css";

const TOOLTIP_WIDTH = 340;
const TOOLTIP_HEIGHT = 280;
const HIGHLIGHT_PAD = 16;

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const GuideTourOverlay = ({
  isOpen,
  steps,
  currentStep,
  onClose,
  onNext,
  onPrev,
}) => {
  const [metrics, setMetrics] = useState(null);
  const step = steps[currentStep] || null;

  useLayoutEffect(() => {
    if (!isOpen || !step) {
      setMetrics(null);
      return undefined;
    }

    let rafId = 0;
    let timeoutId = 0;

    const measure = () => {
      const el = document.querySelector(`[data-tour-id="${step.target}"]`);
      if (!el) return;

      const scrollContainer = el.closest('[data-tour-scroll-container="true"]');
      if (scrollContainer) {
        const scrollerRect = scrollContainer.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const offset =
          elRect.top - scrollerRect.top + scrollContainer.scrollTop - 40;
        scrollContainer.scrollTo({
          top: Math.max(0, offset),
          behavior: "smooth",
        });
      } else {
        el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
      }

      timeoutId = window.setTimeout(() => {
        const rect = el.getBoundingClientRect();
        const paddedRect = {
          left: rect.left - HIGHLIGHT_PAD,
          top: rect.top - HIGHLIGHT_PAD,
          width: rect.width + HIGHLIGHT_PAD * 2,
          height: rect.height + HIGHLIGHT_PAD * 2,
        };

        let tx = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
        let ty = rect.bottom + HIGHLIGHT_PAD + 16;

        if (step.position === "right") {
          tx = rect.right + HIGHLIGHT_PAD + 16;
          ty = rect.top + rect.height / 2 - 80;
        } else if (step.position === "left") {
          tx = rect.left - HIGHLIGHT_PAD - TOOLTIP_WIDTH - 16;
          ty = rect.top + rect.height / 2 - 80;
        } else if (step.position === "top") {
          tx = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
          ty = rect.top - HIGHLIGHT_PAD - 220;
        }

        tx = clamp(tx, 16, window.innerWidth - TOOLTIP_WIDTH - 16);
        ty = clamp(ty, 16, window.innerHeight - TOOLTIP_HEIGHT);

        setMetrics({
          paddedRect,
          tooltipLeft: tx,
          tooltipTop: ty,
        });
      }, 360);
    };

    rafId = window.requestAnimationFrame(measure);
    const handleWindowChange = () => measure();
    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [currentStep, isOpen, step]);

  const clipPath = useMemo(() => {
    if (!metrics?.paddedRect) return "none";
    const { left, top, width, height } = metrics.paddedRect;
    return `polygon(0% 0%, 0% 100%, ${left}px 100%, ${left}px ${top}px, ${
      left + width
    }px ${top}px, ${left + width}px ${top + height}px, ${left}px ${
      top + height
    }px, ${left}px 100%, 100% 100%, 100% 0%)`;
  }, [metrics]);

  if (typeof document === "undefined" || !isOpen || !step || !metrics) {
    return null;
  }

  return createPortal(
    <div className={`${styles.overlay} ${styles.overlayActive}`}>
      <div className={styles.dim} style={{ clipPath }} />
      <div
        className={styles.highlight}
        style={{
          left: metrics.paddedRect.left,
          top: metrics.paddedRect.top,
          width: metrics.paddedRect.width,
          height: metrics.paddedRect.height,
        }}
      />
      <div
        className={styles.tooltip}
        style={{ left: metrics.tooltipLeft, top: metrics.tooltipTop }}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.tooltipInner}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close guide tour"
          >
            ×
          </button>
          <div className={styles.stepNum}>{currentStep + 1}</div>
          <div className={styles.title}>{step.title}</div>
          <p className={styles.desc}>{step.desc}</p>
        </div>
        <div className={styles.footer}>
          <div className={styles.progress}>
            {currentStep + 1} of {steps.length}
          </div>
          <div className={styles.buttons}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSkip}`}
              onClick={onClose}
            >
              Skip tour
            </button>
            {currentStep > 0 ? (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrev}`}
                onClick={onPrev}
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              className={`${styles.button} ${styles.buttonNext}`}
              onClick={onNext}
            >
              {currentStep === steps.length - 1 ? "Get started" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default GuideTourOverlay;
